import React from "react";
import { Dropdown, Pagination, Spin } from "antd";
import {
  DownloadOutlined,
  FileTextOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { Invoice, InvoiceStatus } from "../subscription.types";

interface BillingHistoryTableProps {
  invoices: Invoice[];
  loading: boolean;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onDownloadAll: () => void;
  onDownloadOne: (invoice: Invoice) => void;
}

const BillingHistoryTable: React.FC<BillingHistoryTableProps> = ({
  invoices,
  loading,
  page,
  pageSize,
  total,
  onPageChange,
  onDownloadAll,
  onDownloadOne,
}) => {
  const { t } = useTranslation("subscription");
  const money = (n: number) => `$${n.toFixed(2)}`;
  const statusLabel: Record<InvoiceStatus, string> = {
    paid: t("billingHistory.status.paid"),
    pending: t("billingHistory.status.pending"),
    failed: t("billingHistory.status.failed"),
  };

  return (
    <div className="sub-billing">
      <div className="sub-billing-head">
        <h3 className="sub-billing-title">{t("billingHistory.title")}</h3>
        <button className="sub-download-all" onClick={onDownloadAll}>
          <DownloadOutlined /> {t("billingHistory.downloadAll")}
        </button>
      </div>

      {loading ? (
        <div style={{ padding: "48px 0", textAlign: "center" }}>
          <Spin />
        </div>
      ) : (
        <>
          <table className="sub-table">
            <thead>
              <tr>
                <th>{t("billingHistory.columns.file")}</th>
                <th>{t("billingHistory.columns.startDate")}</th>
                <th>{t("billingHistory.columns.endDate")}</th>
                <th>{t("billingHistory.columns.amountPaid")}</th>
                <th>{t("billingHistory.columns.status")}</th>
                <th style={{ width: 48 }} />
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id}>
                  <td>
                    <span className="sub-file-cell">
                      <FileTextOutlined className="file-ic" />
                      {inv.fileName}
                    </span>
                  </td>
                  <td>{inv.startDate}</td>
                  <td>{inv.endDate}</td>
                  <td>{money(inv.amount)}</td>
                  <td>
                    <span className={`sub-status ${inv.status}`}>
                      <span className="dot" />
                      {statusLabel[inv.status]}
                    </span>
                  </td>
                  <td>
                    <Dropdown
                      trigger={["click"]}
                      menu={{
                        items: [
                          {
                            key: "download",
                            icon: <DownloadOutlined />,
                            label: t("billingHistory.downloadOne"),
                          },
                        ],
                        onClick: () => onDownloadOne(inv),
                      }}
                    >
                      <button
                        className="sub-row-action"
                        aria-label={t("billingHistory.actionsAriaLabel")}
                      >
                        <MoreOutlined />
                      </button>
                    </Dropdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="sub-pagination">
            <Pagination
              current={page}
              pageSize={pageSize}
              total={total}
              showSizeChanger={false}
              onChange={onPageChange}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default BillingHistoryTable;
